/**
 * AgentStatusBadge — F18 T18.3.x.1.
 *
 * Pokazuje status agenta (online <2 min od ostatniego heartbeat / offline / unknown).
 * Polluje `GET /api/erp-integrations/:id/agent-status` co 30 sek przez React Query.
 *
 * Stan:
 *  - `online` — zielona kropka + tekst "Online" + tooltip (agentId, version, X sek temu)
 *  - `offline` — szary tekst "Offline" + tooltip (last seen N min temu / nigdy nie pingował)
 *  - `unknown` — brak danych z endpointu (no heartbeat) — szare "—"
 *
 * Format „last seen": relative time po polsku ("teraz" / "X sek temu" / "X min temu" / „X h temu").
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

const POLL_INTERVAL_MS = 30_000;

interface AgentStatusDto {
  online: boolean;
  agentId: string | null;
  agentVersion: string | null;
  lastSeenAt: string | null;
  ageSeconds: number | null;
}

interface Props {
  integrationId: string;
}

function relativeTime(
  ageSeconds: number,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (ageSeconds < 5) return t('tenant.settings.integrations.agentStatus.now');
  if (ageSeconds < 60)
    return t('tenant.settings.integrations.agentStatus.secAgo', { n: ageSeconds });
  const minutes = Math.floor(ageSeconds / 60);
  if (minutes < 60) return t('tenant.settings.integrations.agentStatus.minAgo', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('tenant.settings.integrations.agentStatus.hourAgo', { n: hours });
  const days = Math.floor(hours / 24);
  return t('tenant.settings.integrations.agentStatus.dayAgo', { n: days });
}

export function AgentStatusBadge({ integrationId }: Props) {
  const { t } = useTranslation();
  const statusQ = useQuery({
    queryKey: ['erp-agent-status', integrationId],
    queryFn: async () => {
      const res = await api.get<AgentStatusDto>(`/erp-integrations/${integrationId}/agent-status`);
      return res.data;
    },
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  const status = statusQ.data;

  if (!status) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-text-muted"
        data-testid={`agent-status-badge-${integrationId}`}
      >
        —
      </span>
    );
  }

  if (status.online) {
    const tooltip = [
      status.agentId &&
        `${t('tenant.settings.integrations.agentStatus.agentLabel')}: ${status.agentId}`,
      status.agentVersion &&
        `${t('tenant.settings.integrations.agentStatus.versionLabel')}: ${status.agentVersion}`,
      status.ageSeconds !== null && relativeTime(status.ageSeconds, t),
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400"
        title={tooltip}
        data-testid={`agent-status-badge-${integrationId}`}
        data-status="online"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
        {t('tenant.settings.integrations.agentStatus.online')}
      </span>
    );
  }

  // Offline — pokazujemy gdy mamy lastSeenAt (kiedyś był), inaczej „nieznany" (no heartbeat ever)
  const offlineTooltip = status.lastSeenAt
    ? `${t('tenant.settings.integrations.agentStatus.lastSeen')}: ${
        status.ageSeconds !== null ? relativeTime(status.ageSeconds, t) : status.lastSeenAt
      }${status.agentId ? ' · ' + status.agentId : ''}`
    : t('tenant.settings.integrations.agentStatus.neverConnected');

  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs text-text-muted"
      title={offlineTooltip}
      data-testid={`agent-status-badge-${integrationId}`}
      data-status={status.lastSeenAt ? 'offline' : 'unknown'}
    >
      <span className="inline-block h-2 w-2 rounded-full bg-gray-400" aria-hidden="true" />
      {status.lastSeenAt
        ? t('tenant.settings.integrations.agentStatus.offline')
        : t('tenant.settings.integrations.agentStatus.notConnected')}
    </span>
  );
}
